// Copyright 2016 Sorama B.V.

import { computedFrom } from "aurelia-framework";
import { HardwareServiceError } from "errors/HardwareServiceError";
import { HttpResponseMessageError } from "errors/HttpResponseMessageError";
import { IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";

export class LocalHardwareService<
    THardwareDevice extends IHardwareDevice,
> implements IHardwareService<THardwareDevice> {
    private apiUri: () => Promise<string>;
    private _hardware: THardwareDevice[] = [];
    private hardwarePromise: Promise<void>;

    protected webRequestHandler: IWebRequestHandler;

    constructor(webRequestHandler: IWebRequestHandler, baseUri: () => Promise<string>) {
        this.webRequestHandler = webRequestHandler;
        this.apiUri = baseUri;
    }

    @computedFrom("_hardware")
    get hardware(): THardwareDevice[] {
        return this._hardware;
    }

    @computedFrom("hardwarePromise")
    get isRefreshing(): boolean {
        return this.hardwarePromise != null;
    }

    public refresh(): Promise<void> {
        // Make sure that we're piggybacking already pending refresh calls.
        if (this.hardwarePromise) {
            return this.hardwarePromise;
        }

        return this.apiUri().then((uri) => {
            return (this.hardwarePromise = this.webRequestHandler
                .createGetRequest<THardwareDevice[]>(uri)
                .withDownloadProgress((progress) => {
                    // Get the current response and try to parse it
                    const request = <XMLHttpRequest>progress.progressEvent.target;
                    const currentResponse = request.responseText;

                    // Try parsing as JSON
                    try {
                        const devices = this.afterRefresh(JSON.parse(currentResponse + "]"));

                        // Don't remove any entries from the existing list until discovery is complete, only add new ones
                        for (const dev of devices) {
                            if (this._hardware.every((x) => x.id != dev.id)) {
                                this._hardware.push(dev);
                            }
                        }
                    } catch (e) {
                        // Ignore parsing errors
                    }
                })
                .send()
                .then((response) => {
                    const devices = this.afterRefresh(response.content);

                    // Replace entire array after refresh
                    this._hardware.splice(0, this._hardware.length, ...devices);

                    this.hardwarePromise = null;
                })
                .catch((reason: HttpResponseMessageError) => {
                    const message: string = reason.message
                        ? reason.message
                        : "Communication error with Acquisition Service";
                    const err = new HardwareServiceError(message);
                    this.hardwarePromise = null;
                    throw err;
                }));
        });
    }

    public getHardwareById(id: string): Promise<THardwareDevice> {
        // Filter for the device
        const findAction: (hw: THardwareDevice[]) => THardwareDevice = (hw: THardwareDevice[]) => {
            for (let i = 0; i < hw.length; i++) {
                if (hw[i].id === id) return hw[i];
            }

            return null;
        };

        if (this.hardware) {
            // First check if it is here
            const device = findAction(this.hardware);
            if (device) return Promise.resolve(device);
        }

        // Devices haven't been enumerated yet or not found, refresh
        return this.refresh().then(() => {
            const device = findAction(this.hardware);
            if (device) return device;

            throw new HardwareServiceError("Device was not found (" + id + ")");
        });
    }

    protected sortHardware(a: THardwareDevice, b: THardwareDevice): number {
        // Sort by name
        let aKey = a.name.toUpperCase();
        let bKey = b.name.toUpperCase();

        if (aKey < bKey) return 1;

        if (aKey > bKey) return -1;

        // Names are equal; sort by ID instead
        aKey = a.id.toUpperCase();
        bKey = b.id.toUpperCase();

        if (aKey < bKey) return 1;

        if (aKey > bKey) return -1;

        // IDs are also equal
        return 0;
    }

    protected afterRefresh(devices: THardwareDevice[]): THardwareDevice[] {
        return devices;
    }
}
